const ALLOWED_FILE_TYPES = ["image/jpeg", "application/pdf", "image/png"];
const ALLOWED_EXTENSIONS = [".jpeg", ".jpg", ".pdf", ".png"];

/**
 * Validates if a file has an allowed type for condition uploads.
 * Checks both file extension and MIME type for robustness.
 *
 * @param file - The file to validate
 * @returns true if file type is allowed, false otherwise
 */
export const isValidFileType = (file: File): boolean => {
  const extension = "." + file.name.split(".").pop()?.toLowerCase();
  const mimeType = file.type;

  return (
    ALLOWED_EXTENSIONS.includes(extension) ||
    ALLOWED_FILE_TYPES.includes(mimeType)
  );
};

/**
 * Generates a user-friendly error message for unsupported file types.
 *
 * @param fileName - The name of the file that was rejected
 * @returns Error message string
 */
export const getFileTypeErrorMessage = (fileName: string): string => {
  return `"${fileName}" is not a supported file type. Please upload a JPEG, PNG, or PDF file.`;
};

/**
 * Converts image files (JPEG/PNG) to PDF format.
 * PDF files are returned as-is.
 *
 * Uses jsPDF for conversion with automatic aspect ratio preservation and centering.
 *
 * @param file - The file to convert
 * @returns Promise resolving to the file (converted or original)
 * @throws Error if image loading fails, file reading fails, or image processing fails
 */
export const convertFileToPdf = async (file: File): Promise<File> => {
  // If already a PDF, return as-is
  if (
    file.type === "application/pdf" ||
    file.name.toLowerCase().endsWith(".pdf")
  ) {
    return file;
  }

  // Import jspdf dynamically to reduce bundle size
  const { jsPDF } = await import("jspdf");

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const img = new Image();

        img.onload = () => {
          // Create PDF with dimensions based on image aspect ratio
          const imgWidth = 210; // A4 width in mm
          const imgHeight = (img.height / img.width) * imgWidth;

          const pdf = new jsPDF({
            orientation: imgHeight > imgWidth ? "portrait" : "landscape",
            unit: "mm",
            format: "a4",
          });

          const pageWidth = pdf.internal.pageSize.getWidth();
          const pageHeight = pdf.internal.pageSize.getHeight();

          // Calculate dimensions to fit page while maintaining aspect ratio
          let finalWidth = pageWidth - 20; // 10mm margin on each side
          let finalHeight = (img.height / img.width) * finalWidth;

          if (finalHeight > pageHeight - 20) {
            finalHeight = pageHeight - 20;
            finalWidth = (img.width / img.height) * finalHeight;
          }

          // Center the image on the page
          const x = (pageWidth - finalWidth) / 2;
          const y = (pageHeight - finalHeight) / 2;

          pdf.addImage(
            e.target?.result as string,
            file.type === "image/png" ? "PNG" : "JPEG",
            x,
            y,
            finalWidth,
            finalHeight,
          );

          // Create a new File object from the PDF blob
          const blob = pdf.output("blob");
          const pdfFile = new File([blob], `${file.name.split(".")[0]}.pdf`, {
            type: "application/pdf",
          });
          resolve(pdfFile);
        };

        img.onerror = () => {
          reject(new Error(`Failed to load image: ${file.name}`));
        };

        img.src = e.target?.result as string;
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        reject(err);
      }
    };

    reader.onerror = () => {
      reject(new Error(`Failed to read file: ${file.name}`));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Validates and processes a file for condition upload.
 * Performs type validation and converts images to PDF format.
 *
 * This is the main entry point for file processing and should be used by UI components.
 *
 * @param file - The file to process
 * @returns Promise resolving to the processed file ready for upload
 * @throws Error if file type is invalid or conversion fails
 *
 * @example
 * try {
 *   const processedFile = await processFileForUpload(file);
 *   // File is ready to upload
 * } catch (error) {
 *   console.error(error.message); // Already has user-friendly message
 * }
 */
export const processFileForUpload = async (file: File): Promise<File> => {
  if (!isValidFileType(file)) {
    throw new Error(getFileTypeErrorMessage(file.name));
  }

  return convertFileToPdf(file);
};
